import { ref, computed } from "vue";

export const MIN_SCALE = 0.5;
export const MAX_SCALE = 5;

export function useCanvasTransform() {
  const scale = ref(1);
  const panPosition = ref({ x: 0, y: 0 });
  const isDragging = ref(false);
  const lastMousePosition = ref({ x: 0, y: 0 });
  const isSpaceKeyPressed = ref(false);

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();

    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(
      MIN_SCALE,
      Math.min(MAX_SCALE, scale.value + delta)
    );

    if (newScale !== scale.value) {
      scale.value = newScale;
      // Reset pan position when scale is 1
      if (newScale === 1) {
        panPosition.value = { x: 0, y: 0 };
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === "Space" && !event.repeat) {
      isSpaceKeyPressed.value = true;
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.code === "Space") {
      isSpaceKeyPressed.value = false;
    }
  };

  const handleMouseDown = (event: MouseEvent) => {
    // Allow middle mouse button (button 1) or left button (button 0) when space is pressed
    if (event.button === 1 || (event.button === 0 && isSpaceKeyPressed.value)) {
      event.preventDefault();
      isDragging.value = true;
      lastMousePosition.value = { x: event.clientX, y: event.clientY };
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging.value) {
      event.preventDefault();
      const deltaX = event.clientX - lastMousePosition.value.x;
      const deltaY = event.clientY - lastMousePosition.value.y;

      panPosition.value = {
        x: panPosition.value.x + deltaX,
        y: panPosition.value.y + deltaY,
      };

      lastMousePosition.value = { x: event.clientX, y: event.clientY };
    }
  };

  const handleMouseUp = (event: MouseEvent) => {
    if (event.button === 1 || (event.button === 0 && isSpaceKeyPressed.value)) {
      isDragging.value = false;
    }
  };

  const transformStyle = computed(() => ({
    transform: `translate(${panPosition.value.x}px, ${panPosition.value.y}px)`,
  }));

  const scaleStyle = computed(() => ({
    transform: `scale(${scale.value})`,
    transformOrigin: "center center",
  }));

  return {
    scale,
    panPosition,
    transformStyle,
    scaleStyle,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleKeyDown,
    handleKeyUp,
    isSpaceKeyPressed,
  };
}
